/**
 * SettingsPanel - Audio & Preferences Settings
 */

import React, { useState } from 'react';
import CONFIG from '../../config';

function SettingsPanel({
  onClose,
  audioDevices,
  selectedInput,
  selectedOutput,
  onInputChange,
  onOutputChange,
  onRefreshDevices
}) {
  const [activeTab, setActiveTab] = useState('audio');
  const [testingAudio, setTestingAudio] = useState(false);
  const [audioTestError, setAudioTestError] = useState(null);

  // Generate a clean 1 kHz test tone via Web Audio API and play it through
  // the currently selected output device. This avoids a file dependency and
  // gives a deterministic tone regardless of OS/browser.
  //
  // Strategy: oscillator -> gain -> MediaStreamDestination -> Audio element
  // (so we can use setSinkId on the Audio element to route to a specific
  // output device).
  const testAudio = async () => {
    setAudioTestError(null);
    setTestingAudio(true);
    let ctx, osc, gain, dest, audio;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) throw new Error('Web Audio API not supported in this browser');
      ctx = new AC();
      if (ctx.state === 'suspended') await ctx.resume();
      osc = ctx.createOscillator();
      gain = ctx.createGain();
      gain.gain.value = 0;
      osc.frequency.value = 1000;
      osc.type = 'sine';
      osc.connect(gain);
      // Route through MediaStreamDestination so we can pick output sink
      dest = ctx.createMediaStreamDestination();
      gain.connect(dest);
      audio = new Audio();
      audio.srcObject = dest.stream;
      audio.autoplay = true;

      // Pick the chosen speaker if the browser supports it
      if (selectedOutput && typeof audio.setSinkId === 'function') {
        try {
          await audio.setSinkId(selectedOutput);
        } catch (e) {
          // setSinkId can throw if user hasn't granted permission - tolerate
          console.warn('[CX] setSinkId failed:', e);
        }
      }

      osc.start();
      const now = ctx.currentTime;
      // Click-free 1.5s envelope (10ms fade in, hold, 50ms fade out)
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.15, now + 0.01);
      gain.gain.setValueAtTime(0.15, now + 1.4);
      gain.gain.linearRampToValueAtTime(0, now + 1.5);

      await new Promise(res => setTimeout(res, 1600));
      try { osc.stop(); } catch {}
      try { audio.pause(); } catch {}
      try { await ctx.close(); } catch {}
      setTestingAudio(false);
    } catch (err) {
      console.error('[CX] Audio test failed:', err);
      setAudioTestError(err.message || String(err));
      setTestingAudio(false);
      try { osc && osc.stop(); } catch {}
      try { audio && audio.pause(); } catch {}
      try { ctx && ctx.close(); } catch {}
    }
  };
  
  return (
    <div className="cx-dialog-overlay" onClick={onClose}>
      <div className="cx-dialog cx-dialog--large" onClick={(e) => e.stopPropagation()}>
        <div className="cx-dialog__header">
          <h3 className="cx-dialog__title">⚙️ Settings</h3>
          <button className="cx-dialog__close" onClick={onClose}>
            ✕
          </button>
        </div>
        
        {/* Tabs */}
        <div className="cx-settings__tabs">
          <button
            className={`cx-settings__tab ${activeTab === 'audio' ? 'cx-settings__tab--active' : ''}`}
            onClick={() => setActiveTab('audio')}
          >
            🔊 Audio
          </button>
          <button
            className={`cx-settings__tab ${activeTab === 'notifications' ? 'cx-settings__tab--active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            🔔 Notifications
          </button>
          <button
            className={`cx-settings__tab ${activeTab === 'shortcuts' ? 'cx-settings__tab--active' : ''}`}
            onClick={() => setActiveTab('shortcuts')}
          >
            ⌨️ Shortcuts
          </button>
          {CONFIG.mediaHelper?.enabled && (
            <button
              className={`cx-settings__tab ${activeTab === 'helper' ? 'cx-settings__tab--active' : ''}`}
              onClick={() => setActiveTab('helper')}
            >
              🖥️ Media Helper
            </button>
          )}
        </div>
        
        <div className="cx-dialog__body">
          {/* ═══════════════════════════════════════════════════════════
              AUDIO TAB
              ═══════════════════════════════════════════════════════════ */}
          {activeTab === 'audio' && (
            <div className="cx-settings__section">
              <div className="cx-settings__row">
                <label className="cx-settings__label">🎤 Microphone</label>
                <select
                  className="cx-settings__select"
                  value={selectedInput || ''}
                  onChange={(e) => onInputChange(e.target.value)}
                >
                  {audioDevices.inputs.map(device => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="cx-settings__row">
                <label className="cx-settings__label">🔊 Speaker</label>
                <div className="cx-settings__select-row">
                  <select
                    className="cx-settings__select"
                    value={selectedOutput || ''}
                    onChange={(e) => onOutputChange(e.target.value)}
                  >
                    {audioDevices.outputs.map(device => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                      </option>
                    ))}
                  </select>
                  <button
                    className="cx-settings__test-btn"
                    onClick={testAudio}
                    disabled={testingAudio}
                    title="Play a 1 kHz tone through the selected speaker"
                  >
                    {testingAudio ? '🔊 Playing…' : '🔊 Test'}
                  </button>
                </div>
                {audioTestError && (
                  <div className="cx-settings__error">
                    Audio test failed: {audioTestError}
                  </div>
                )}
              </div>
              
              <div className="cx-settings__row">
                <button
                  className="cx-settings__refresh-btn"
                  onClick={onRefreshDevices}
                >
                  🔄 Refresh Devices
                </button>
              </div>
              
              <div className="cx-settings__divider" />
              
              <div className="cx-settings__row">
                <label className="cx-settings__label">Audio Processing</label>
              </div>
              
              <div className="cx-settings__checkbox-row">
                <input
                  type="checkbox"
                  id="echoCancellation"
                  defaultChecked={CONFIG.audio.echoCancellation}
                />
                <label htmlFor="echoCancellation">Echo Cancellation</label>
              </div>
              
              <div className="cx-settings__checkbox-row">
                <input
                  type="checkbox"
                  id="noiseSuppression"
                  defaultChecked={CONFIG.audio.noiseSuppression}
                />
                <label htmlFor="noiseSuppression">Noise Suppression</label>
              </div>
              
              <div className="cx-settings__checkbox-row">
                <input
                  type="checkbox"
                  id="autoGainControl"
                  defaultChecked={CONFIG.audio.autoGainControl}
                />
                <label htmlFor="autoGainControl">Auto Gain Control</label>
              </div>
            </div>
          )}
          
          {/* ═══════════════════════════════════════════════════════════
              NOTIFICATIONS TAB
              ═══════════════════════════════════════════════════════════ */}
          {activeTab === 'notifications' && (
            <div className="cx-settings__section">
              <div className="cx-settings__checkbox-row">
                <input
                  type="checkbox"
                  id="desktopNotifications"
                  defaultChecked={CONFIG.notifications.desktop}
                />
                <label htmlFor="desktopNotifications">Desktop Notifications</label>
              </div>
              
              <div className="cx-settings__checkbox-row">
                <input
                  type="checkbox"
                  id="soundNotifications"
                  defaultChecked={CONFIG.notifications.sound}
                />
                <label htmlFor="soundNotifications">Sound on Incoming Call</label>
              </div>
              
              <div className="cx-settings__checkbox-row">
                <input
                  type="checkbox"
                  id="tabFlash"
                  defaultChecked={CONFIG.notifications.tabFlash}
                />
                <label htmlFor="tabFlash">Flash Browser Tab</label>
              </div>
              
              <div className="cx-settings__divider" />
              
              <div className="cx-settings__row">
                <label className="cx-settings__label">Ringtone Volume</label>
                <input
                  type="range"
                  className="cx-settings__slider"
                  min="0"
                  max="100"
                  defaultValue={CONFIG.notifications.volume * 100}
                />
              </div>
              
              <div className="cx-settings__row">
                <label className="cx-settings__label">After Call Work Timer</label>
                <select className="cx-settings__select cx-settings__select--small">
                  <option value="30">30 seconds</option>
                  <option value="60" selected>60 seconds</option>
                  <option value="90">90 seconds</option>
                  <option value="120">2 minutes</option>
                  <option value="0">Disabled</option>
                </select>
              </div>
            </div>
          )}
          
          {/* ═══════════════════════════════════════════════════════════
              SHORTCUTS TAB
              ═══════════════════════════════════════════════════════════ */}
          {activeTab === 'shortcuts' && (
            <div className="cx-settings__section">
              <div className="cx-settings__shortcuts">
                <div className="cx-settings__shortcut">
                  <span className="cx-settings__shortcut-key">Enter</span>
                  <span className="cx-settings__shortcut-desc">Answer Call</span>
                </div>
                <div className="cx-settings__shortcut">
                  <span className="cx-settings__shortcut-key">Esc</span>
                  <span className="cx-settings__shortcut-desc">Hang Up / Reject</span>
                </div>
                <div className="cx-settings__shortcut">
                  <span className="cx-settings__shortcut-key">M</span>
                  <span className="cx-settings__shortcut-desc">Toggle Mute</span>
                </div>
                <div className="cx-settings__shortcut">
                  <span className="cx-settings__shortcut-key">H</span>
                  <span className="cx-settings__shortcut-desc">Toggle Hold</span>
                </div>
                <div className="cx-settings__shortcut">
                  <span className="cx-settings__shortcut-key">T</span>
                  <span className="cx-settings__shortcut-desc">Transfer</span>
                </div>
                <div className="cx-settings__shortcut">
                  <span className="cx-settings__shortcut-key">D</span>
                  <span className="cx-settings__shortcut-desc">Toggle Dialpad</span>
                </div>
              </div>
            </div>
          )}
          
          {/* ═══════════════════════════════════════════════════════════
              MEDIA HELPER TAB (VDI)
              ═══════════════════════════════════════════════════════════ */}
          {activeTab === 'helper' && (
            <div className="cx-settings__section">
              <div className="cx-settings__info-box">
                <strong>🖥️ CX Media Helper</strong>
                <p>
                  For VDI environments, install the CX Media Helper on your local 
                  computer to improve audio quality by routing media directly.
                </p>
              </div>
              
              <div className="cx-settings__row">
                <label className="cx-settings__label">Status</label>
                <span className="cx-settings__status cx-settings__status--disconnected">
                  ● Not Connected
                </span>
              </div>
              
              <div className="cx-settings__checkbox-row">
                <input
                  type="checkbox"
                  id="enableHelper"
                  defaultChecked={CONFIG.mediaHelper?.enabled}
                />
                <label htmlFor="enableHelper">Enable Media Helper</label>
              </div>
              
              <div className="cx-settings__checkbox-row">
                <input
                  type="checkbox"
                  id="fallbackVdi"
                  defaultChecked={CONFIG.mediaHelper?.fallbackToVdi}
                />
                <label htmlFor="fallbackVdi">Fallback to VDI audio if helper unavailable</label>
              </div>
              
              <div className="cx-settings__divider" />
              
              <a 
                href="/downloads/cx-media-helper.exe" 
                className="cx-settings__download-btn"
              >
                ⬇️ Download CX Media Helper
              </a>
            </div>
          )}
        </div>
        
        <div className="cx-dialog__footer">
          <button
            className="cx-dialog__btn cx-dialog__btn--primary"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default SettingsPanel;
